import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import { Box } from '@mui/system'
import { CircularProgress, Collapse, IconButton, Tooltip, useTheme } from '@mui/material'
import { ChevronRight, DescriptionOutlined, QuizOutlined } from '@mui/icons-material'
import { capitalize } from '../../../../utils/functions'
import CustomLinearProgress from '../../../../components/muiComponents/customLinearProgress'
import {
    TestAnalysisState,
    VideoAnalysisState,
    VideoNotesAnalysisState,
    WatchedVideoLessonState,
} from '../../../../features/types'
import { testApiSlice } from '../../../../features/section/testSlice'
import { videoLessonApiSlice } from '../../../../features/section/videoLessonSlice'
import { TEST, VIDEO } from '../../../../utils/constants'
import Section from './Section'
import RoundedCheck from '../../../../assets/icons/RoundedCheck.svg'
import { ui } from '../../../../utils/uiTokens'
import { Level, LEVEL_COLORS } from '../../ratingPage/ratingLevel'

interface Props {
    topic: VideoAnalysisState | TestAnalysisState
    studentId: number
    type: string
    conspectByVideo?: Map<string, VideoNotesAnalysisState>
}

const TopicProgressCard: React.FC<Props> = ({ topic, type, studentId, conspectByVideo }) => {
    const theme = useTheme()
    const [expanded, setExpanded] = useState(false)
    const { formatMessage, locale } = useIntl()
    const { data: tests = [], isFetching: isFetchingTests } =
        testApiSlice.useGetStudentTestAnalysisQuery(
            {
                courseTopicId: topic.courseTopicId,
                studentId,
            },
            { skip: !expanded },
        )

    const { data: videoLessons = [], isFetching: isFetchingVideoLessons } =
        videoLessonApiSlice.useGetStudentVideoLessonAnalysisQuery(
            {
                courseTopicId: topic.courseTopicId,
                studentId,
            },
            { skip: !expanded || type === TEST },
        )

    // На вкладке видео тесты темы идут следом за её видеоуроками, в одном блоке
    const sections =
        type === VIDEO
            ? [
                  ...videoLessons.map((section) => ({ section, type: VIDEO })),
                  ...tests.map((section) => ({ section, type: TEST })),
              ]
            : tests.map((section) => ({ section, type: TEST }))
    const styles = {
        wrapper(open: boolean): React.CSSProperties {
            return {
                border: `1px solid ${ui.border}`,
                borderRadius: '12px',
                marginBottom: '12px',
                background: ui.card,
            }
        },
        container: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
            p: 1,
            pr: 2,
        },
        chevron: {
            transform: expanded ? 'rotate(90deg)' : 'rotate(0)',
            transition: '0.15s ease-in-out',
        },
        flexCenter: {
            display: 'flex',
            alignItems: 'center',
        },
        flexBetween: {
            display: 'flex',
            justifyContent: 'space-between',
        },
        topicProgress: {
            display: 'flex',
            alignItems: 'center',
            // на телефоне полоска и значки уходят под название темы
            flexWrap: { xs: 'wrap', md: 'nowrap' },
            gap: 2,
            flexGrow: 1,
            marginLeft: 1,
        },
        topicTitle: {
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 0.5,
            flexGrow: 1,
            fontSize: '14.5px',
            color: ui.ink,
        },
        progressCell: {
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            flexShrink: 0,
            flexBasis: { xs: '100%', md: 'auto' },
        },
        progressBar: {
            width: { xs: 80, md: 160 },
            height: 8,
            borderRadius: 999,
        },
        percent: {
            minWidth: 44,
            textAlign: 'right',
            fontWeight: 600,
            fontSize: '13.5px',
        },
        linearProgress: {
            backgroundcolor: ui.track,
            barbackgroundcolor: ui.pink,
        },
        countVideo: {
            fontSize: '12.5px',
            fontWeight: 600,
            padding: '3px 10px',
            backgroundColor: topic.percentage ? ui.pinkSoft : ui.track,
            color: topic.percentage ? ui.pink : ui.muted,
            borderRadius: '100px',
            marginLeft: '8px',
            whiteSpace: 'nowrap' as const,
        },
    }

    const getSectionCount = () => {
        if (type === VIDEO) {
            return (
                (topic as VideoAnalysisState)?.countVideo +
                ' ' +
                formatMessage({ id: 'video' })
            )
        }

        return `${(topic as TestAnalysisState)?.countTest || 0} ${formatMessage({ id: 'test' })}`;
    }

    // Видео -> конспект -> тест: зелёный - сделано, жёлтый - частично, красный - нет
    const video = topic as VideoAnalysisState
    const conspectLevel: Level = !video.conspectCount ? 'red' : video.conspectCount >= video.countVideo ? 'green' : 'yellow'
    const testDone = video.testDone || 0
    const testLevel: Level = !testDone ? 'red' : testDone >= (video.countTest || 0) && (video.testPercent || 0) >= 70 ? 'green' : 'yellow'
    const statusChip = (level: Level) => ({
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 64,
        gap: 0.5,
        px: 1,
        py: 0.25,
        borderRadius: '100px',
        fontSize: '12.5px',
        fontWeight: 600,
        whiteSpace: 'nowrap',
        background: LEVEL_COLORS[level].bg,
        color: LEVEL_COLORS[level].text,
    })

    const getTestCount = () =>
        `${tests.length} ${formatMessage({ id: 'test' })}`

    return (
        <Box sx={styles.wrapper(expanded)}>
            <Box
                sx={styles.container}
                onClick={() => {
                    setExpanded(!expanded)
                }}
            >
                <Box sx={{ ...styles.flexCenter, width: '95%' }}>
                    <IconButton>
                        <ChevronRight sx={styles.chevron} />
                    </IconButton>

                    <Box sx={styles.topicProgress}>
                        <Box sx={styles.topicTitle}>
                            {topic[`topicTitle${capitalize(locale)}`]}
                            <Box style={styles.countVideo}>{getSectionCount()}</Box>
                            {type === VIDEO && tests.length > 0 && (
                                <Box style={styles.countVideo}>{getTestCount()}</Box>
                            )}
                        </Box>

                        <Box sx={styles.progressCell}>
                            <CustomLinearProgress
                                variant="determinate"
                                value={topic.percentage}
                                sx={styles.progressBar}
                                {...styles.linearProgress}
                            />

                            <Box
                                sx={{
                                    ...styles.percent,
                                    color: topic.percentage ? ui.ink : ui.muted,
                                }}
                            >
                                {topic.percentage}%
                            </Box>
                            {type === VIDEO && video.countVideo > 0 && (
                                <Tooltip title={formatMessage({ id: 'conspect' })}>
                                    <Box sx={statusChip(conspectLevel)}>
                                        <DescriptionOutlined sx={{ fontSize: 15 }} />
                                        {video.conspectCount || 0}/{video.countVideo}
                                    </Box>
                                </Tooltip>
                            )}
                            {type === VIDEO && ((video.countTest || 0) > 0 ? (
                                <Tooltip title={formatMessage({ id: 'topicTest' })}>
                                    <Box sx={statusChip(testLevel)}>
                                        <QuizOutlined sx={{ fontSize: 15 }} />
                                        {testDone ? `${video.testPercent}%` : '—'}
                                    </Box>
                                </Tooltip>
                            ) : (
                                // пустое место той же ширины, чтобы полоски видео стояли ровно
                                <Box sx={{ minWidth: 64 }} />
                            ))}
                        </Box>
                    </Box>
                </Box>
                {topic.percentage === 100 && <img src={RoundedCheck} alt="RoundedCheck" />}
            </Box>

            <Collapse in={expanded}>
                {isFetchingTests || isFetchingVideoLessons ? (
                    <CircularProgress sx={{ m: 1 }} />
                ) : (
                    <Box>
                        {sections.map((item, index) => (
                            <Section
                                key={index}
                                section={item.section}
                                studentId={studentId}
                                isLast={index === sections.length - 1}
                                type={item.type}
                                conspect={
                                    item.type === VIDEO
                                        ? conspectByVideo?.get(
                                              `id:${(item.section as WatchedVideoLessonState).videoLessonId}`,
                                          ) ||
                                          conspectByVideo?.get(
                                              `t:${(item.section as WatchedVideoLessonState).title}`,
                                          )
                                        : undefined
                                }
                            />
                        ))}
                    </Box>
                )}
            </Collapse>
        </Box>
    )
}

export default TopicProgressCard
